import React, { useEffect, useMemo, useState } from 'react';
import { leadCrm, telecallers as telecallerApi } from '../../services/dealerApi';
import { Loader2, TrendingUp, RefreshCw, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';

const M = (v) => (v ?? 0).toLocaleString();
const PCT = (v) => `${Math.round((v ?? 0) * 100)}%`;

// --- Date range helpers (local timezone; we emit YYYY-MM-DD which the backend
// parses as ISO. All boundaries are inclusive on `from`, exclusive on `to`.) ---
const iso = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const dy = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfNextMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1);
// ISO week: Monday-based
const startOfWeek = (d) => {
  const nd = new Date(d);
  const day = nd.getDay(); // 0=Sun..6=Sat
  const diff = (day + 6) % 7; // days since Monday
  nd.setDate(nd.getDate() - diff);
  nd.setHours(0, 0, 0, 0);
  return nd;
};

const PRESETS = [
  { key: 'all', label: 'All time' },
  { key: 'this_week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'custom', label: 'Custom' },
];

const rangeForPreset = (key) => {
  const now = new Date();
  switch (key) {
    case 'this_week': {
      const from = startOfWeek(now);
      const to = new Date(from); to.setDate(to.getDate() + 7);
      return { from: iso(from), to: iso(to) };
    }
    case 'last_week': {
      const thisMon = startOfWeek(now);
      const from = new Date(thisMon); from.setDate(from.getDate() - 7);
      return { from: iso(from), to: iso(thisMon) };
    }
    case 'this_month': {
      const from = startOfMonth(now);
      const to = startOfNextMonth(now);
      return { from: iso(from), to: iso(to) };
    }
    case 'last_month': {
      const from = startOfMonth(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const to = startOfMonth(now);
      return { from: iso(from), to: iso(to) };
    }
    default:
      return { from: '', to: '' };
  }
};

export default function Pipeline() {
  const [metrics, setMetrics] = useState(null);
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tcs, setTcs] = useState([]);
  const [selectedTc, setSelectedTc] = useState('');

  // Date filter state
  const [preset, setPreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const activeRange = useMemo(() => {
    if (preset === 'custom') return { from: customFrom, to: customTo };
    return rangeForPreset(preset);
  }, [preset, customFrom, customTo]);

  const load = () => {
    setLoading(true);
    // For "custom" without both dates typed, still hit backend with what's set.
    const range = {};
    if (activeRange.from) range.from = activeRange.from;
    // Backend treats `to` as exclusive; if user picked a custom end date they
    // expect it to be inclusive, so nudge one day forward.
    if (activeRange.to) {
      if (preset === 'custom') {
        const dt = new Date(activeRange.to);
        dt.setDate(dt.getDate() + 1);
        range.to = iso(dt);
      } else {
        range.to = activeRange.to;
      }
    }
    leadCrm.pipeline(selectedTc || undefined, range)
      .then((r) => { setMetrics(r.metrics); setScope(r.scope); })
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load pipeline'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [selectedTc, preset, customFrom, customTo]);

  useEffect(() => {
    // Telecallers don't have permission to list telecallers — skip the call
    // to avoid a 403 console error. The dropdown is hidden for them anyway.
    if (scope?.actor_type === 'telecaller') { setTcs([]); return; }
    telecallerApi.list().then((r) => setTcs(r.telecallers || [])).catch(() => setTcs([]));
  }, [scope?.actor_type]);

  const isTelecallerView = scope?.actor_type === 'telecaller';

  const rangeLabel = useMemo(() => {
    if (preset === 'all') return 'All time';
    if (!activeRange.from && !activeRange.to) return 'All time';
    const p = PRESETS.find((x) => x.key === preset);
    if (preset !== 'custom' && p) return p.label;
    if (activeRange.from && activeRange.to) return `${activeRange.from} → ${activeRange.to}`;
    if (activeRange.from) return `From ${activeRange.from}`;
    if (activeRange.to) return `Until ${activeRange.to}`;
    return 'Custom';
  }, [preset, activeRange]);

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-cyan-400 font-bold">Reporting</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1">Pipeline</h1>
          <p className="text-slate-400 text-sm mt-1">
            {isTelecallerView ? 'Your marketplace lead performance.' : 'Full marketplace lead pipeline across all telecallers.'}
            <span className="ml-2 inline-flex items-center gap-1 text-cyan-300">
              <CalendarRange className="w-3.5 h-3.5" /> {rangeLabel}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isTelecallerView && (
            <select
              value={selectedTc}
              onChange={(e) => setSelectedTc(e.target.value)}
              className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700 text-sm text-slate-200 focus:border-cyan-500 outline-none"
              data-testid="pipeline-telecaller-filter"
            >
              <option value="">All telecallers</option>
              {tcs.map((tc) => <option key={tc.id} value={tc.id}>{tc.name}</option>)}
            </select>
          )}
          <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-700 text-sm text-slate-300" data-testid="pipeline-refresh">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </header>

      {/* Date range filter */}
      <section className="p-3 rounded-2xl bg-slate-900/40 border border-slate-800" data-testid="pipeline-date-filter">
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => {
            const active = preset === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                data-testid={`pipeline-preset-${p.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? 'bg-cyan-500/20 border-cyan-400/60 text-cyan-200'
                    : 'bg-slate-950/40 border-slate-700 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-200'
                }`}
              >
                {p.label}
              </button>
            );
          })}
          {preset === 'custom' && (
            <div className="flex items-center gap-2 ml-1" data-testid="pipeline-custom-range">
              <label className="text-xs text-slate-400">From</label>
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="px-2 py-1.5 rounded-lg bg-slate-950/60 border border-slate-700 text-sm text-slate-200 focus:border-cyan-500 outline-none"
                data-testid="pipeline-custom-from"
              />
              <label className="text-xs text-slate-400">To</label>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="px-2 py-1.5 rounded-lg bg-slate-950/60 border border-slate-700 text-sm text-slate-200 focus:border-cyan-500 outline-none"
                data-testid="pipeline-custom-to"
              />
            </div>
          )}
        </div>
      </section>

      {loading || !metrics ? (
        <div className="min-h-[300px] flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
        </div>
      ) : (
        <>
          {/* Hero KPI: conversion rate */}
          <div className="p-5 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-slate-900 to-slate-950 border border-cyan-500/30">
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-bold flex items-center gap-2">
              <TrendingUp className="w-4 h-4" /> Conversion Rate
            </div>
            <div className="text-5xl font-extrabold text-white tabular-nums mt-1">{PCT(metrics.conversion_rate)}</div>
            <div className="text-sm text-slate-400 mt-1">
              {M(metrics.converted)} converted / {M(metrics.total_leads)} total leads
              {metrics.avg_time_to_first_contact_minutes != null && (
                <> · Avg time to first contact: <span className="text-slate-200 font-semibold">{metrics.avg_time_to_first_contact_minutes} min</span></>
              )}
            </div>
          </div>

          {/* Pipeline grid */}
          <section>
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Pipeline stages</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              <Tile label="Total" value={metrics.total_leads} />
              <Tile label="New" value={metrics.new_leads} tone="cyan" />
              <Tile label="Contacted" value={metrics.contacted} tone="blue" />
              <Tile label="Converted" value={metrics.converted} tone="emerald" />
              <Tile label="Not Interested" value={metrics.not_interested} tone="slate" />
            </div>
          </section>

          {/* Call outcomes */}
          <section>
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Call outcomes</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Tile label="Not Answering" value={metrics.not_answering} tone="amber" />
              <Tile label="Busy" value={metrics.busy} tone="amber" />
              <Tile label="Switched Off" value={metrics.switched_off} tone="slate" />
              <Tile label="Invalid Number" value={metrics.invalid_number} tone="red" />
            </div>
          </section>

          {/* Interest breakdown */}
          <section>
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Interest levels</h2>
            <div className="grid grid-cols-3 gap-2">
              <Tile label="High" value={metrics.high_interest} tone="emerald" />
              <Tile label="Medium" value={metrics.medium_interest} tone="amber" />
              <Tile label="Low" value={metrics.low_interest} tone="slate" />
            </div>
          </section>

          {/* Task pipeline */}
          <section>
            <h2 className="text-sm font-bold text-white uppercase tracking-wider mb-2">Scheduled activities</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Tile label="Follow-ups Today" value={metrics.followups_due_today} tone="amber" />
              <Tile label="Overdue Follow-ups" value={metrics.overdue_followups} tone="red" pulse={metrics.overdue_followups > 0} />
              <Tile label="Test Drives Scheduled" value={metrics.test_drives_scheduled} tone="blue" />
              <Tile label="Showroom Visits Scheduled" value={metrics.showroom_visits_scheduled} tone="emerald" />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

const TONE = {
  cyan: 'text-cyan-300 border-cyan-500/40 bg-cyan-500/5',
  amber: 'text-amber-300 border-amber-500/40 bg-amber-500/5',
  blue: 'text-blue-300 border-blue-500/40 bg-blue-500/5',
  emerald: 'text-emerald-300 border-emerald-500/40 bg-emerald-500/5',
  red: 'text-red-300 border-red-500/40 bg-red-500/5',
  slate: 'text-slate-300 border-slate-800 bg-slate-900/40',
};

function Tile({ label, value, tone = 'slate', pulse = false }) {
  return (
    <div className={`p-3 rounded-xl border ${TONE[tone]} ${pulse ? 'animate-pulse' : ''}`} data-testid={`pipeline-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-2xl font-extrabold tabular-nums">{M(value)}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-80 mt-0.5">{label}</div>
    </div>
  );
}
